/**
 * Configurações → Pessoas (D12) — quem entra, quem muda de papel, quem sai.
 *
 * ── Por que esta tela é a que destrava a atribuição ───────────────────────
 *
 * A API da D12 existe inteira desde agosto; o que nunca existiu foi a tela. E
 * sem ela ninguém era provisionado — então, no diálogo de passar uma conversa,
 * a revenda inteira aparecia com "ainda não tem acesso ao atendimento" e
 * "ainda não tem cadastro no CRM", e a transferência não tinha para quem ir.
 *
 * ── Os DOIS eixos de engine ficam na tabela, e não escondidos ─────────────
 *
 * `status` responde "esta pessoa faz parte da revenda?". `vinculoNoCrm` e
 * `acessoAoAtendimento` respondem outra coisa: "as engines já sabem quem ela
 * é?". Misturá-los num só foi o que escondeu o problema até agora — a pessoa
 * entrava como `ativo`, porque daqui estava mesmo, e ninguém via que faltava
 * metade. Quem descobria era o vendedor, dias depois, levando recusa.
 *
 * Por isso as duas colunas existem e ficam LADO A LADO do papel: quem convida
 * precisa ver o buraco na hora, e não ser informado dele por terceiros.
 *
 * ── O CRM não se cria daqui, e a tela diz isso ────────────────────────────
 *
 * O Twenty recusa `createWorkspaceMember` para chave de API — medido contra a
 * v2.26.1 em 24/08/2026, e registrado em
 * `docs/decisoes/2026-08-24-membro-do-twenty-nao-se-cria-por-api.md`. O passo
 * do CRM VINCULA por e-mail a um cadastro que já exista no painel dele.
 *
 * Isso é um passo manual permanente, não uma lacuna nossa. Esconder faria
 * quem administra clicar em "completar" para sempre esperando um resultado que
 * não vem; dito, ele sabe o que fazer no painel do CRM.
 *
 * ── "Completar" é um botão só, e não um por engine ────────────────────────
 *
 * Do lado de quem administra a pergunta é uma: "esta pessoa está incompleta,
 * completa aí". Dois botões obrigariam a diagnosticar qual motor falta antes de
 * saber qual apertar — e o diagnóstico é justamente o que a pessoa não tem.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BYTES_POR_GB, ErroDaApi } from '@cliqcrm/contracts';
import type {
  AcessoAoAtendimento,
  Pessoa,
  PessoaConvidada,
  RedefinicaoDeSenha,
  VinculoNoCrm,
} from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import {
  useApi,
  useCarteiraDaPessoa,
  useLimitesDaOrganizacao,
  usePessoas,
  useTimes,
  useUnidades,
} from '@/shared/api/consultas';
import { Carregando, Erro, Vazio } from '@/shared/ui/estados/estados';
import { NOMES_DE_PAPEL, nomeDoPapel } from '@/entities/acesso/papel';
import {
  Avatar,
  Botao,
  Campo,
  CampoSelecao,
  Chip,
  Dialogo,
  ListaLinha,
  MenuAcoes,
  Selo,
  Tabela,
} from '@/shared/ui';
import { nomeDoProvedor } from '@/entities/integracoes/provedor';
import { AcaoDaSecao } from './acao-da-secao';
import {
  DialogoDeMaisArmazenamento,
  DialogoDoLimite,
  FaixaDoArmazenamento,
  VagasDaOrganizacao,
} from './limites-da-organizacao';
import { recusaDoLimite, valorAceito } from '../lib/limites-da-organizacao';
import type { Dinheiro, RecusaDoLimite } from '../model/limites-da-organizacao';
import { RedefinirSenha } from './redefinir-senha';
import { DialogoDeStepUp, useStepUp } from '@/features/acesso/confirmar-identidade';
import { PrimeiroAcessoDaPessoa } from '@/entities/acesso/pessoa';
import { Faixa } from '@/shared/ui/estados/faixa';
import { useSessao } from '@/shared/sessao/contexto';

/** Os papéis que uma revenda atribui. `operacao_cliqfy` e `sistema` são nossos. */
const PAPEIS_DA_REVENDA = ['agente', 'vendedor', 'gestor', 'admin_tenant'] as const;

/**
 * O alcance de cada papel, em uma linha.
 *
 * Quem convida escolhe o papel de alguém que vai trabalhar oito horas por dia
 * com o que ele decidir aqui — e "gestor" contra "vendedor" não diz, sozinho,
 * o que muda. A frase é o que evita a escolha por hierarquia de organograma.
 */
const ALCANCE_DO_PAPEL: Record<string, string> = {
  agente: 'atende conversas da própria carteira; não mexe no funil.',
  vendedor: 'a própria carteira: conversas, clientes e oportunidades dela.',
  gestor: 'a unidade inteira, e redistribui o que está em aberto.',
  admin_tenant: 'a organização inteira, mais usuários, integrações e exportação.',
};

const CARTEIRAS = [
  { valor: 'propria', rotulo: 'A própria — só o que é dela' },
  { valor: 'time', rotulo: 'O time' },
  { valor: 'unidade', rotulo: 'A unidade' },
  { valor: 'tenant', rotulo: 'A organização inteira' },
] as const;

/** O selo de cada eixo, e o que ele quer dizer para quem lê. */
function SeloDoCrm({ vinculo }: { vinculo: VinculoNoCrm }): ReactNode {
  if (vinculo === 'ligado') return <Selo tom="ok">ligado</Selo>;
  return (
    <Selo
      tom="atencao"
      title="Sem isto a pessoa não recebe conversa nem responde por oportunidade."
    >
      pendente
    </Selo>
  );
}

function SeloDoAtendimento({ acesso }: { acesso: AcessoAoAtendimento }): ReactNode {
  if (acesso === 'ligado') return <Selo tom="ok">ligado</Selo>;
  if (acesso === 'nao_se_aplica') return <span className="admin__sem">não se aplica</span>;
  if (acesso === 'desconhecido') {
    return (
      <Selo tom="neutro" title="Não foi possível verificar agora. Tente de novo em instantes.">
        não sei
      </Selo>
    );
  }
  return (
    <Selo tom="atencao" title="Sem isto, passar conversa para esta pessoa não tem efeito.">
      pendente
    </Selo>
  );
}

export function SecaoPessoas(): ReactNode {
  const consulta = usePessoas();
  const [convidando, definirConvidando] = useState(false);
  /*
   * A pessoa recém-convidada fica aqui até alguém fechar a janela, porque num
   * dos desfechos ela carrega uma senha que não se recupera depois.
   */
  const [primeiroAcesso, definirPrimeiroAcesso] = useState<PessoaConvidada | null>(null);
  const [editando, definirEditando] = useState<Pessoa | null>(null);
  const [desativando, definirDesativando] = useState<Pessoa | null>(null);
  const [redistribuindo, definirRedistribuindo] = useState<Pessoa | null>(null);
  const [redefinindo, definirRedefinindo] = useState<Pessoa | null>(null);
  /** A senha redefinida fica aqui até alguém fechar a janela, como a do convite. */
  const [senhaRedefinida, definirSenhaRedefinida] = useState<{
    readonly nome: string;
    readonly resultado: RedefinicaoDeSenha;
  } | null>(null);
  const [recado, definirRecado] = useState<string | null>(null);
  /** A reativação recusada pelo limite de usuários: o diálogo de contratar (#768). */
  const [limiteDaReativacao, definirLimiteDaReativacao] = useState<{
    readonly pessoa: Pessoa;
    readonly recusa: RecusaDoLimite;
  } | null>(null);

  const api = useApi();
  const cache = useQueryClient();
  // As vagas mudam com a equipe (#768): o "N de M usuários" relê junto.
  const recarregar = () => {
    void cache.invalidateQueries({ queryKey: chaves.pessoas() });
    void cache.invalidateQueries({ queryKey: chaves.limitesDaOrganizacao() });
  };
  const { estado, recarregarMe } = useSessao();
  // Os números do contrato, só para quem contrata ou faz a gestão (decisão de 28/10/2026).
  const limites = useLimitesDaOrganizacao(
    estado.nome === 'autenticado' &&
      (estado.me.papel === 'admin_tenant' || estado.me.papel === 'super_admin'),
  );

  /* Mais armazenamento (#718): o diálogo de contratar, e a confirmação de que já vale. */
  const [contratandoArmazenamento, definirContratandoArmazenamento] = useState(false);
  const [armazenamentoContratado, definirArmazenamentoContratado] = useState(false);
  const contratarArmazenamento = useMutation({
    mutationFn: async (valor: Dinheiro) => {
      if (!api) throw new Error('Sem sessão.');
      return api.contratarArmazenamento({ corpo: { valor_aceito: valorAceito(valor) } });
    },
    onSuccess: (novos) => {
      // A resposta já são os limites com o bloco: a faixa muda sem esperar a releitura.
      cache.setQueryData(chaves.limitesDaOrganizacao(), novos);
      definirContratandoArmazenamento(false);
      definirArmazenamentoContratado(true);
      // O recado de uma tentativa que falhou antes contradiria a faixa verde.
      definirRecado(null);
    },
    onError: (erro: unknown) => {
      // O valor que mudou, o preço que saiu, o papel que não contrata: o servidor diz por
      // quê, e os limites relidos trazem o valor de hoje para a próxima tentativa.
      definirContratandoArmazenamento(false);
      void cache.invalidateQueries({ queryKey: chaves.limitesDaOrganizacao() });
      definirRecado(
        recadoDeErro(erro, { generica: 'Não foi possível contratar agora. Tente de novo.' }),
      );
    },
  });

  /*
   * O step-up da redefinição de senha mora aqui, e não vem da página: a seção é
   * montada sozinha em mais de um lugar, e o diálogo dele só abre quando a
   * redefinição pede.
   */
  const stepUp = useStepUp();
  const euMesmo = estado.nome === 'autenticado' ? estado.me.usuario.id : null;
  /** A clínica (ADR-0022): a marca de acesso ao prontuário só existe com ela ligada. */
  const clinica = estado.nome === 'autenticado' && (estado.me.flags['clinica'] ?? false);

  const prontuario = useMutation({
    mutationFn: async (pessoa: Pessoa) => {
      if (!api) throw new Error('Sem sessão.');
      return api.definirAcessoAoProntuario({
        caminho: { id: pessoa.id },
        corpo: { ligado: !(pessoa.acessoAoProntuario ?? false) },
      });
    },
    onSuccess: (resultado, pessoa) => {
      recarregar();
      // A marca vai no `/v1/me` da pessoa, que pode ser quem está mudando.
      void recarregarMe();
      definirRecado(
        resultado.ligado
          ? `${pessoa.nome} passa a ver o prontuário dos pacientes.`
          : `${pessoa.nome} deixa de ver o prontuário dos pacientes.`,
      );
    },
    onError: () => definirRecado('Não foi possível mudar o acesso ao prontuário. Tente de novo.'),
  });

  /**
   * Completar nas engines — a segunda chance do convite.
   *
   * O convite toca as engines UMA vez, e os dois casos que ficam para trás são
   * os NORMAIS: o CRM só vincula ao que já existe naquele segundo, e o
   * atendimento só é tocado se a revenda já tiver ligado a integração — o que
   * costuma acontecer depois de a equipe estar montada.
   */
  const completar = useMutation({
    mutationFn: async (pessoa: Pessoa) => {
      if (!api) throw new Error('Sem sessão.');
      return api.reprovisionarPessoa({ caminho: { id: pessoa.id } });
    },
    onSuccess: (resultado) => {
      recarregar();
      /*
       * O RESULTADO, e não o relatório por peça.
       *
       * Aqui saía o nome de cada provedor colado ao detalhe cru da rota — o
       * vocabulário interno inteiro, numa linha, para quem só quis completar um
       * cadastro. Sucesso parcial continua sendo dito, porque ele muda o que a
       * pessoa faz em seguida: ela olha as colunas.
       */
      const pendentes = resultado.engines.filter((e) => e.resultado === 'falhou').length;
      definirRecado(
        pendentes === 0
          ? 'Cadastro completo. Confira as colunas ao lado.'
          : 'Parte do cadastro foi completada. Confira as colunas ao lado.',
      );
    },
    onError: () => definirRecado('Não foi possível completar agora. Tente de novo.'),
  });

  const reativar = useMutation({
    mutationFn: async ({ pessoa, contratar }: { pessoa: Pessoa; contratar?: Dinheiro }) => {
      if (!api) throw new Error('Sem sessão.');
      return api.reativarPessoa({
        caminho: { id: pessoa.id },
        corpo: contratar ? { contratarUsuarioAdicional: valorAceito(contratar) } : {},
      });
    },
    onSuccess: (resultado) => {
      definirLimiteDaReativacao(null);
      recarregar();
      // Desativada de novo enquanto o acesso voltava (#768): ela continua desativada.
      definirRecado(
        resultado.status === 'ativo'
          ? 'Acesso reativado.'
          : 'A pessoa foi desativada de novo enquanto o acesso voltava, e continua desativada.',
      );
    },
    onError: (erro: unknown, { pessoa }) => {
      // No limite de usuários, o diálogo de contratar; o resto vira o recado (#768).
      const doLimite = recusaDoLimite(erro);
      if (doLimite) {
        definirLimiteDaReativacao({ pessoa, recusa: doLimite });
        return;
      }
      definirLimiteDaReativacao(null);
      definirRecado(recadoDeErro(erro, { generica: 'Não foi possível reativar. Tente de novo.' }));
    },
  });

  if (consulta.isPending)
    return <Carregando forma="lista" linhas={5} rotulo="Carregando a equipe" />;
  // Só sem nada na tela: com dados carregados, uma atualização que falhou
  // (o limite de requisições, a rede) não apaga o que estava certo.
  if (consulta.isError && consulta.data === undefined) {
    return (
      <Erro
        erro={consulta.error}
        aoRepetir={() => void consulta.refetch()}
        titulo="Não foi possível carregar a equipe"
      />
    );
  }

  const pessoas = consulta.data?.itens ?? [];

  /*
   * O menu de ações é o MESMO na tabela e na lista do celular.
   *
   * Duplicá-lo era a alternativa, e é a que envelhece: uma ação nova entra num
   * dos dois lugares, e a metade das pessoas que usa o outro nunca a vê.
   */
  const acoesDe = (pessoa: Pessoa): ReactNode => (
    <MenuAcoes
      rotulo={`Ações de ${pessoa.nome}`}
      itens={[
        { rotulo: 'Alterar papel e lotação', aoClicar: () => definirEditando(pessoa) },
        {
          rotulo: completar.isPending ? 'Completando…' : 'Completar cadastro',
          aoClicar: () => completar.mutate(pessoa),
          /*
           * Só faz sentido para quem tem buraco. Oferecer a quem está
           * completo convidaria a clicar e receber "nada mudou" —
           * duas idas às engines para não fazer nada.
           */
          ...(pessoa.vinculoNoCrm === 'ligado' && pessoa.acessoAoAtendimento === 'ligado'
            ? { desabilitado: true, motivo: 'O cadastro desta pessoa já está completo.' }
            : {}),
        },
        {
          rotulo: 'Redistribuir carteira',
          aoClicar: () => definirRedistribuindo(pessoa),
          separadorAntes: true,
          /*
           * Sem vínculo com o CRM não há como saber de quem é cada
           * negócio — e o diálogo abriria só para dizer isso. O
           * motivo aqui evita a viagem.
           */
          ...(pessoa.vinculoNoCrm === 'ligado'
            ? {}
            : {
                desabilitado: true,
                motivo: 'Com o cadastro incompleto, não dá para saber o que é dela.',
              }),
        },
        ...(clinica
          ? [
              {
                rotulo: pessoa.acessoAoProntuario
                  ? 'Retirar acesso ao prontuário'
                  : 'Liberar acesso ao prontuário',
                aoClicar: () => prontuario.mutate(pessoa),
                separadorAntes: true,
              },
            ]
          : []),
        {
          rotulo: 'Redefinir senha',
          aoClicar: () => definirRedefinindo(pessoa),
          separadorAntes: true,
          ...semRedefinicao(pessoa, euMesmo),
        },
        pessoa.status === 'suspenso'
          ? {
              rotulo: 'Reativar acesso',
              aoClicar: () => reativar.mutate({ pessoa }),
            }
          : {
              rotulo: 'Desativar acesso',
              aoClicar: () => definirDesativando(pessoa),
              perigo: true,
              // O papel é da equipe da Cliqfy, e quem o tira é ela (decisão de 30/10/2026).
              ...(pessoa.superAdmin
                ? {
                    desabilitado: true,
                    motivo:
                      'Quem é Super Admin da Cliqfy não tem o acesso desativado pela organização.',
                  }
                : {}),
            },
      ]}
    />
  );

  return (
    <section className="admin__secao" aria-label="Pessoas">
      {/* Sobe para a fileira do título. Ver `acao-da-secao.tsx`. */}
      <AcaoDaSecao>
        {/* Sempre, e não só no limite (#768). Sem contrato não há limite, e não aparece. */}
        {limites.data?.usuarios ? (
          <VagasDaOrganizacao
            emUso={limites.data.usuarios.em_uso}
            contratados={limites.data.usuarios.contratados}
          />
        ) : null}
        <Botao variante="primario" onClick={() => definirConvidando(true)}>
          Convidar pessoa
        </Botao>
      </AcaoDaSecao>

      <p className="admin__ajuda">
        Convidar cria a conta de acesso e libera o atendimento. O cadastro comercial é{' '}
        <strong>ligado</strong> pelo e-mail, e não criado aqui se ele ainda não existir, a coluna
        Cadastro fica pendente, e “Completar cadastro” liga os dois quando ele existir.
      </p>

      {/*
        O aviso do armazenamento, a partir de 90% (#717), com o bloco que o
        administrador contrata por aqui (#718). Sem o valor do GB adicional no
        contrato, ou para o Super Admin, a faixa diz com quem falar.
      */}
      {limites.data?.armazenamento ? (
        <FaixaDoArmazenamento
          emUsoGb={
            limites.data.armazenamento.em_uso_bytes === null
              ? null
              : limites.data.armazenamento.em_uso_bytes / BYTES_POR_GB
          }
          contratadoGb={limites.data.armazenamento.contratado_gb}
          tamanhoDoBlocoGb={limites.data.armazenamento.bloco_gb}
          podeContratar={
            limites.data.armazenamento.pode_contratar &&
            limites.data.armazenamento.valor_do_bloco !== null
          }
          aoContratar={() => definirContratandoArmazenamento(true)}
          acabouDeContratar={armazenamentoContratado}
          recado={
            limites.data.armazenamento.pode_contratar
              ? 'O valor do armazenamento adicional deste contrato ainda não foi definido: fale com a equipe da Cliqfy.'
              : estado.nome === 'autenticado' && estado.me.papel === 'super_admin'
                ? 'Para passar dele, aumente o armazenamento contratado na gestão de organizações.'
                : undefined
          }
        />
      ) : null}
      {contratandoArmazenamento && limites.data?.armazenamento ? (
        <DialogoDeMaisArmazenamento
          tamanhoDoBlocoGb={limites.data.armazenamento.bloco_gb}
          valorDoBloco={limites.data.armazenamento.valor_do_bloco}
          enviando={contratarArmazenamento.isPending}
          aoContratar={() => {
            const valor = limites.data?.armazenamento?.valor_do_bloco;
            if (valor) contratarArmazenamento.mutate(valor);
          }}
          aoFechar={() => definirContratandoArmazenamento(false)}
        />
      ) : null}

      {recado && (
        <p className="admin__nota admin__nota--info" role="status">
          {recado}
        </p>
      )}

      {pessoas.length === 0 ? (
        <Vazio
          titulo="Ninguém na equipe ainda"
          porque="Convide a primeira pessoa. Sem alguém com o cadastro completo, não dá para passar conversa nem oportunidade para ninguém."
          acao={{ rotulo: 'Convidar pessoa', aoClicar: () => definirConvidando(true) }}
        />
      ) : (
        <>
          {/*
          Duas peças, e não uma tabela encolhida.

          A tabela tem SEIS colunas. Em 390px ela precisaria de ~900px: ia para
          o celular como tabela e rolava de lado, com o nome da pessoa saindo da
          vista junto — quem rola para ver a coluna "Atendimento" perde de quem
          ela é.

          O corte é 1024 e não 720 pela mesma razão da tela de Clientes: com o
          menu aberto, 1024px de janela deixam ~720px de conteúdo.
        */}
          <div className="admin__so-mouse">
            <Tabela
              rotulo="Pessoas da organização"
              densa
              colunas={[
                { chave: 'pessoa', rotulo: 'Pessoa' },
                { chave: 'papel', rotulo: 'Papel' },
                { chave: 'lotacao', rotulo: 'Unidade / time' },
                { chave: 'crm', rotulo: 'Cadastro', titulo: 'Cadastro comercial ligado' },
                {
                  chave: 'atendimento',
                  rotulo: 'Atendimento',
                  titulo: 'Pode receber e responder conversas',
                },
                { chave: 'acoes', rotulo: 'Ações', rotuloOculto: true },
              ]}
              chaveDe={(linha) => String(linha['id'])}
              linhas={pessoas.map((pessoa) => ({
                id: pessoa.id,
                pessoa: (
                  <span className="admin__pessoa">
                    <span className="admin__pessoa-nome">{pessoa.nome}</span>
                    <span className="admin__pessoa-email">{pessoa.email}</span>
                    {pessoa.status === 'suspenso' && <Chip tom="erro">acesso suspenso</Chip>}
                    {pessoa.status === 'falha_no_provisionamento' && (
                      <Chip tom="erro">falhou ao provisionar</Chip>
                    )}
                    {clinica && pessoa.acessoAoProntuario && (
                      <Chip title="Vê o resumo do prontuário dos pacientes">prontuário</Chip>
                    )}
                  </span>
                ),
                papel: nomeDoPapel(pessoa.papel),
                lotacao: (
                  <span className="admin__sem">
                    {[pessoa.unidadeNome, pessoa.timeNome].filter(Boolean).join(' / ') || '—'}
                  </span>
                ),
                crm: <SeloDoCrm vinculo={pessoa.vinculoNoCrm} />,
                atendimento: <SeloDoAtendimento acesso={pessoa.acessoAoAtendimento} />,
                acoes: acoesDe(pessoa),
              }))}
            />
          </div>

          <ul className="admin__lista admin__so-cel">
            {pessoas.map((pessoa) => (
              <ListaLinha
                key={pessoa.id}
                densa
                controles
                /*
                 * A linha inteira abre "alterar papel", que é o primeiro item
                 * do menu. É o que `--controles` pede: sem alvo, uma linha de
                 * 44px no dedo não faz nada e só o botão miúdo do canto faz —
                 * exatamente ao contrário do que o tamanho promete.
                 */
                aoAbrir={() => definirEditando(pessoa)}
                rotulo={`Alterar papel e lotação de ${pessoa.nome}`}
                inicio={<Avatar nome={pessoa.nome} tamanho={32} foto={pessoa.fotoUrl} />}
                nome={pessoa.nome}
                previa={
                  [nomeDoPapel(pessoa.papel), pessoa.unidadeNome, pessoa.timeNome]
                    .filter(Boolean)
                    .join(' · ') || '—'
                }
                /* Os dois selos de engine na terceira sub-linha: são o motivo
                   de alguém abrir esta tela, e não cabem na linha do nome. */
                meta={
                  <span className="admin__lista-meta">
                    <SeloDoCrm vinculo={pessoa.vinculoNoCrm} />
                    <SeloDoAtendimento acesso={pessoa.acessoAoAtendimento} />
                    {pessoa.status === 'suspenso' && <Chip tom="erro">acesso suspenso</Chip>}
                    {pessoa.status === 'falha_no_provisionamento' && (
                      <Chip tom="erro">falhou ao provisionar</Chip>
                    )}
                  </span>
                }
                acoes={acoesDe(pessoa)}
              />
            ))}
          </ul>
        </>
      )}

      {limiteDaReativacao && (
        <DialogoDoLimite
          contratados={limiteDaReativacao.recusa.contratados}
          valorDoAdicional={limiteDaReativacao.recusa.valorDoAdicional}
          podeContratar={limiteDaReativacao.recusa.podeContratar}
          verbo="reativar"
          valorMudou={limiteDaReativacao.recusa.valorMudou}
          recado={limiteDaReativacao.recusa.recado}
          enviando={reativar.isPending}
          aoContratar={() => {
            const { pessoa, recusa } = limiteDaReativacao;
            if (recusa.valorDoAdicional) {
              reativar.mutate({ pessoa, contratar: recusa.valorDoAdicional });
            }
          }}
          aoFechar={() => definirLimiteDaReativacao(null)}
        />
      )}

      {convidando && (
        <Convite
          aoFechar={() => definirConvidando(false)}
          aoConvidar={(pessoa) => {
            definirConvidando(false);
            recarregar();
            /*
             * A tela do primeiro acesso substitui o recado, e não se soma a ele.
             *
             * Num dos desfechos há uma senha que aparece UMA vez; um recado que
             * some sozinho ao lado dela competiria pela atenção de quem precisa
             * copiá-la antes de fechar.
             *
             * O `if` não é paranoia: durante uma implantação a web nova conversa
             * com a API antiga por alguns segundos, e ela ainda não devolve este
             * campo. Sem a conferência, quem convidasse nessa janela veria a tela
             * quebrar — depois de a pessoa já ter sido criada.
             */
            if (pessoa.primeiroAcesso) {
              definirPrimeiroAcesso(pessoa);
              return;
            }
            definirRecado(`${pessoa.nome} entrou. Confira as colunas Cadastro e Atendimento.`);
          }}
        />
      )}

      {primeiroAcesso && (
        <PrimeiroAcessoDaPessoa
          nome={primeiroAcesso.nome}
          acesso={primeiroAcesso.primeiroAcesso}
          aoFechar={() => definirPrimeiroAcesso(null)}
        />
      )}

      {editando && (
        <AlterarPapel
          pessoa={editando}
          aoFechar={() => definirEditando(null)}
          aoAlterar={() => {
            definirEditando(null);
            recarregar();
            // O papel vai no `/v1/me`, e a pessoa alterada pode ser quem altera.
            void recarregarMe();
            definirRecado('Papel e lotação atualizados.');
          }}
        />
      )}

      {redistribuindo && (
        <Redistribuicao
          origem={redistribuindo}
          equipe={pessoas}
          aoFechar={() => definirRedistribuindo(null)}
          aoMover={(texto) => {
            definirRedistribuindo(null);
            recarregar();
            definirRecado(texto);
          }}
        />
      )}

      {desativando && (
        <Desativacao
          pessoa={desativando}
          aoFechar={() => definirDesativando(null)}
          aoDesativar={(texto) => {
            definirDesativando(null);
            recarregar();
            definirRecado(texto);
          }}
        />
      )}

      {redefinindo && (
        <RedefinirSenha
          pessoa={redefinindo}
          stepUp={stepUp}
          aoFechar={() => definirRedefinindo(null)}
          aoRedefinir={(resultado) => {
            definirRedefinindo(null);
            definirSenhaRedefinida({ nome: redefinindo.nome, resultado });
          }}
        />
      )}

      {senhaRedefinida && (
        <PrimeiroAcessoDaPessoa
          nome={senhaRedefinida.nome}
          acesso={senhaRedefinida.resultado}
          momento="redefinicao"
          sessoesEncerradas={senhaRedefinida.resultado.sessoesEncerradas}
          aoFechar={() => definirSenhaRedefinida(null)}
        />
      )}

      <DialogoDeStepUp uso={stepUp} />
    </section>
  );
}

/**
 * Por que "Redefinir senha" não está disponível para esta pessoa, ou nada.
 *
 * O servidor recusa os mesmos casos; aqui é para a pessoa saber antes de
 * escrever a justificativa. A recusa que o servidor não explica (a conta em
 * outra organização) fica com ele: a lista não sabe disso, e não deve saber.
 */
function semRedefinicao(
  pessoa: Pessoa,
  euMesmo: string | null,
): { desabilitado: true; motivo: string } | Record<string, never> {
  if (pessoa.usuarioId === euMesmo) {
    return {
      desabilitado: true,
      motivo: 'A sua senha não se redefine por aqui. Peça a outra pessoa que administra a conta.',
    };
  }
  if (pessoa.papel === 'operacao_cliqfy' || pessoa.superAdmin) {
    return { desabilitado: true, motivo: 'A senha da equipe da Cliqfy não se redefine por aqui.' };
  }
  if (pessoa.status === 'suspenso') {
    return { desabilitado: true, motivo: 'Reative o acesso antes de redefinir a senha.' };
  }
  if (pessoa.status !== 'ativo') {
    return {
      desabilitado: true,
      motivo: 'O convite desta pessoa não terminou, e ainda não há senha para redefinir.',
    };
  }
  return {};
}

/** O formulário de convite — nome, e-mail, papel, carteira e lotação. */
function Convite({
  aoFechar,
  aoConvidar,
}: {
  aoFechar: () => void;
  aoConvidar: (pessoa: PessoaConvidada) => void;
}): ReactNode {
  const api = useApi();
  const unidades = useUnidades();
  const times = useTimes();
  const [nome, definirNome] = useState('');
  const [email, definirEmail] = useState('');
  const [papel, definirPapel] = useState<string>('vendedor');
  const [carteira, definirCarteira] = useState<string>('propria');
  const [unidadeId, definirUnidade] = useState('');
  const [timeId, definirTime] = useState('');
  const [recusa, definirRecusa] = useState<string | null>(null);
  /*
   * O aviso de conta existente é OUTRO estado, e não uma recusa.
   *
   * O servidor devolve `CONFIRMACAO_EXIGIDA` quando o e-mail já tem conta em
   * outra revenda. Confirmando, a MESMA conta passa a entrar também nesta,
   * com a senha de sempre — não nasce conta nova desde o realm compartilhado. Mostrar isso em vermelho, junto das recusas, diria "deu
   * errado" para algo que dá certo — e o botão continuaria dizendo "Convidar",
   * como se repetir fosse insistir no mesmo.
   */
  const [contaExistente, definirContaExistente] = useState<string | null>(null);
  /** No limite de usuários, o diálogo de contratar, por cima do convite (#768). */
  const [limite, definirLimite] = useState<RecusaDoLimite | null>(null);

  const convidar = useMutation({
    mutationFn: async ({
      confirmarSegundaConta,
      contratar,
    }: {
      confirmarSegundaConta: boolean;
      contratar?: Dinheiro;
    }) => {
      if (!api) throw new Error('Sem sessão.');
      return api.convidarPessoa({
        corpo: {
          nome: nome.trim(),
          email: email.trim().toLowerCase(),
          papel: papel as never,
          carteira: carteira as never,
          ...(confirmarSegundaConta ? { confirmarSegundaConta } : {}),
          ...(contratar ? { contratarUsuarioAdicional: valorAceito(contratar) } : {}),
          ...(unidadeId ? { unidadeId } : {}),
          ...(timeId ? { timeId } : {}),
        },
        /*
         * Chave nova a cada tentativa, inclusive na confirmada.
         *
         * A confirmação muda o CORPO, e repetir a chave com corpo diferente é
         * `IDEMPOTENCIA_DIVERGENTE` — a pessoa clicaria "Criar mesmo assim" e
         * receberia um erro sobre um cabeçalho que ela não sabe que existe.
         */
        idempotencyKey: crypto.randomUUID(),
      });
    },
    onSuccess: (pessoa) => aoConvidar(pessoa),
    onError: (erro: unknown) => {
      // O limite de usuários também pede confirmação, mas é outra pergunta (#768).
      const doLimite = recusaDoLimite(erro);
      if (doLimite) {
        definirLimite(doLimite);
        return;
      }
      definirLimite(null);
      if (erro instanceof ErroDaApi && erro.codigo === 'CONFIRMACAO_EXIGIDA') {
        definirContaExistente(erro.problema.detail ?? null);
        return;
      }
      definirRecusa(recadoDeErro(erro, { generica: 'Não foi possível convidar. Tente de novo.' }));
    },
  });

  /* Mexer no e-mail desfaz a pergunta: ela era sobre o e-mail de antes. */
  const trocarEmail = (valor: string) => {
    definirEmail(valor);
    definirContaExistente(null);
  };

  /*
   * O time só aparece com a unidade escolhida, e a lista é filtrada por ela.
   *
   * Um time pertence a uma unidade, e a API recusa o par incoerente. Mostrar a
   * lista inteira faria a pessoa escolher algo que o servidor devolve como
   * erro — e o erro chegaria depois de ela já ter preenchido o resto.
   */
  const timesDaUnidade = (times.data?.itens ?? []).filter(
    (time) => !unidadeId || time.unidadeId === unidadeId,
  );

  const podeEnviar = nome.trim() !== '' && email.trim().includes('@') && !convidar.isPending;

  return (
    <>
      <Dialogo
        titulo="Convidar pessoa"
        forma="lateral"
        aoFechar={aoFechar}
        botoes={
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              Cancelar
            </Botao>
            {/*
            O botão MUDA de texto depois da pergunta.

            Continuar dizendo "Convidar" faria o segundo clique parecer o
            primeiro repetido — e é o clique que abre esta organização a uma
            conta que já existe. O texto é o último lugar onde dá para dizer o
            que vai acontecer.
          */}
            <Botao
              variante="primario"
              desabilitado={!podeEnviar}
              motivo="Informe o nome e um e-mail válido."
              onClick={() => convidar.mutate({ confirmarSegundaConta: contaExistente !== null })}
            >
              {convidar.isPending
                ? 'Convidando…'
                : contaExistente
                  ? 'Dar acesso a esta organização'
                  : 'Convidar'}
            </Botao>
          </>
        }
      >
        <Campo
          rotulo="Nome"
          type="text"
          value={nome}
          maxLength={120}
          autoFocus
          onChange={(e) => definirNome(e.currentTarget.value)}
        />
        <Campo
          rotulo="E-mail"
          type="email"
          value={email}
          maxLength={200}
          ajuda="É por ele que o cadastro comercial é ligado. Use o mesmo e-mail dos dois lados."
          onChange={(e) => trocarEmail(e.currentTarget.value)}
        />

        {/*
        A pergunta fica JUNTO do e-mail, que é o campo que a provocou — e em
        tom de atenção, não de erro: o pedido não falhou, ele espera resposta.
      */}
        {contaExistente && <Faixa tom="atencao">{contaExistente}</Faixa>}

        <CampoSelecao
          rotulo="Papel"
          value={papel}
          ajuda={ALCANCE_DO_PAPEL[papel]}
          onChange={(e) => definirPapel(e.currentTarget.value)}
        >
          {PAPEIS_DA_REVENDA.map((valor) => (
            <option key={valor} value={valor}>
              {NOMES_DE_PAPEL[valor] ?? valor}
            </option>
          ))}
        </CampoSelecao>

        <CampoSelecao
          rotulo="O que ela enxerga"
          value={carteira}
          onChange={(e) => definirCarteira(e.currentTarget.value)}
        >
          {CARTEIRAS.map((c) => (
            <option key={c.valor} value={c.valor}>
              {c.rotulo}
            </option>
          ))}
        </CampoSelecao>

        <CampoSelecao
          rotulo="Unidade"
          value={unidadeId}
          onChange={(e) => {
            definirUnidade(e.currentTarget.value);
            // Trocar de unidade solta o time: o par anterior deixaria de ser
            // coerente, e a API o recusaria no envio.
            definirTime('');
          }}
        >
          <option value="">Nenhuma</option>
          {(unidades.data?.itens ?? []).map((unidade) => (
            <option key={unidade.id} value={unidade.id}>
              {unidade.nome}
            </option>
          ))}
        </CampoSelecao>

        {timesDaUnidade.length > 0 && (
          <CampoSelecao
            rotulo="Time"
            value={timeId}
            onChange={(e) => definirTime(e.currentTarget.value)}
          >
            <option value="">Nenhum</option>
            {timesDaUnidade.map((time) => (
              <option key={time.id} value={time.id}>
                {time.nome}
              </option>
            ))}
          </CampoSelecao>
        )}

        {recusa && (
          <p className="dialogo__erro" role="alert">
            {recusa}
          </p>
        )}
      </Dialogo>
      {/*
        O diálogo do limite fica FORA do painel do convite (#1160): o painel entra
        com animação de `transform`, e um ancestral com `transform` vira a
        referência do véu `position: fixed`: o véu cobriria só o painel, e o
        clique na metade de fora fecharia o convite com o que a pessoa digitou. A
        pergunta da conta, quando houve, vai de novo junto com a contratação.
      */}
      {limite && (
        <DialogoDoLimite
          contratados={limite.contratados}
          valorDoAdicional={limite.valorDoAdicional}
          podeContratar={limite.podeContratar}
          valorMudou={limite.valorMudou}
          recado={limite.recado}
          enviando={convidar.isPending}
          aoContratar={() => {
            if (limite.valorDoAdicional) {
              convidar.mutate({
                confirmarSegundaConta: contaExistente !== null,
                contratar: limite.valorDoAdicional,
              });
            }
          }}
          aoFechar={() => definirLimite(null)}
        />
      )}
    </>
  );
}

function AlterarPapel({
  pessoa,
  aoFechar,
  aoAlterar,
}: {
  pessoa: Pessoa;
  aoFechar: () => void;
  aoAlterar: () => void;
}): ReactNode {
  const api = useApi();
  const [papel, definirPapel] = useState<string>(pessoa.papel);
  const [carteira, definirCarteira] = useState<string>(pessoa.carteira);
  const [recusa, definirRecusa] = useState<string | null>(null);

  const alterar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.alterarPessoa({
        caminho: { id: pessoa.id },
        corpo: { papel: papel as never, carteira: carteira as never },
      });
    },
    onSuccess: aoAlterar,
    onError: (erro: unknown) =>
      definirRecusa(recadoDeErro(erro, { generica: 'Não foi possível alterar.' })),
  });

  return (
    <Dialogo
      titulo={`Papel de ${pessoa.nome}`}
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            desabilitado={alterar.isPending}
            motivo="Salvando…"
            onClick={() => alterar.mutate()}
          >
            Salvar
          </Botao>
        </>
      }
    >
      <CampoSelecao
        rotulo="Papel"
        value={papel}
        ajuda={ALCANCE_DO_PAPEL[papel]}
        onChange={(e) => definirPapel(e.currentTarget.value)}
      >
        {PAPEIS_DA_REVENDA.map((valor) => (
          <option key={valor} value={valor}>
            {NOMES_DE_PAPEL[valor] ?? valor}
          </option>
        ))}
      </CampoSelecao>

      <CampoSelecao
        rotulo="O que ela enxerga"
        value={carteira}
        onChange={(e) => definirCarteira(e.currentTarget.value)}
      >
        {CARTEIRAS.map((c) => (
          <option key={c.valor} value={c.valor}>
            {c.rotulo}
          </option>
        ))}
      </CampoSelecao>

      {recusa && (
        <p className="dialogo__erro" role="alert">
          {recusa}
        </p>
      )}
    </Dialogo>
  );
}

/**
 * Desativar, com justificativa — e a resposta diz o que cada engine fez.
 *
 * Não é `{ ok: true }`: a Armadilha da D12 é a desativação que PARECE ter
 * funcionado. Se o provedor de identidade recusou, a pessoa continua entrando,
 * e uma tela dizendo "desativado" mentiria sobre isso.
 */
function Desativacao({
  pessoa,
  aoFechar,
  aoDesativar,
}: {
  pessoa: Pessoa;
  aoFechar: () => void;
  aoDesativar: (recado: string) => void;
}): ReactNode {
  const api = useApi();
  const [justificativa, definirJustificativa] = useState('');
  const [recusa, definirRecusa] = useState<string | null>(null);

  const desativar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.desativarPessoa({
        caminho: { id: pessoa.id },
        corpo: { justificativa: justificativa.trim() },
      });
    },
    onSuccess: (resultado) =>
      aoDesativar(
        resultado.alerta
          ? `Atenção: ${resultado.engines
              .filter((e) => e.resultado === 'falhou')
              .map((e) => `${nomeDoProvedor(e.engine)} — ${e.detalhe}`)
              .join(' · ')}`
          : `${pessoa.nome} perdeu o acesso em todos os sistemas.`,
      ),
    onError: (erro: unknown) =>
      definirRecusa(recadoDeErro(erro, { generica: 'Não foi possível desativar.' })),
  });

  return (
    <Dialogo
      titulo={`Desativar ${pessoa.nome}`}
      aoFechar={aoFechar}
      consequencia
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="perigo"
            desabilitado={justificativa.trim().length < 10 || desativar.isPending}
            motivo="Escreva ao menos dez caracteres de justificativa."
            onClick={() => desativar.mutate()}
          >
            {desativar.isPending ? 'Desativando…' : 'Desativar'}
          </Botao>
        </>
      }
    >
      <p>
        Ela perde o acesso ao produto, ao atendimento e ao CRM. As conversas{' '}
        <strong>continuam no nome dela</strong> — trocam de dono na próxima interação, por decisão
        de produto. O que precisa ser redistribuído à mão é oportunidade em aberto.
      </p>

      <Campo
        rotulo="Por quê?"
        type="text"
        value={justificativa}
        maxLength={500}
        ajuda="Quem e quando a trilha já registra. O porquê é o que ninguém reconstrói depois."
        onChange={(e) => definirJustificativa(e.currentTarget.value)}
      />

      {recusa && (
        <p className="dialogo__erro" role="alert">
          {recusa}
        </p>
      )}
    </Dialogo>
  );
}

/**
 * Passar em lote os negócios em aberto de uma pessoa para outra.
 *
 * ── Só NEGÓCIO, e não conversa ────────────────────────────────────────────
 *
 * Por decisão do dono do produto, de 24/08/2026: desativar deixa as conversas
 * no nome de quem saiu, e o dono só troca na próxima interação. Redistribuir
 * conversa produziria trinta delas trocando de mão num dia, para gente que não
 * participou de nenhuma — e o histórico passaria a mentir sobre quem falou com
 * quem.
 *
 * O que trava sem dono é a OPORTUNIDADE: negócio parado não avança, e ninguém
 * liga para um cliente que está na lista de um vendedor que já saiu.
 *
 * ── O número é lido ANTES e devolvido no envio ────────────────────────────
 *
 * `esperado` é a trava de concorrência. Entre a pessoa ler "são trinta" e
 * clicar, alguém pode ter criado mais dez — e "mover tudo que houver" é como
 * se movem quarenta achando que eram trinta. Se a conta mudar, o servidor
 * recusa e a tela relê.
 *
 * Ele sai da MESMA consulta que a redistribuição usa. Duas contas parecidas
 * divergiriam no primeiro ajuste, e o sintoma seria a tela oferecendo mover
 * trinta enquanto o servidor conta vinte e oito e recusa para sempre.
 *
 * ── O impedimento aparece antes da justificativa ──────────────────────────
 *
 * A resposta traz o obstáculo como campo, e não como erro. Com ele à vista
 * ninguém digita dez linhas de justificativa para descobrir no fim que a
 * origem nem tem vínculo com o CRM.
 */
function Redistribuicao({
  origem,
  equipe,
  aoFechar,
  aoMover,
}: {
  origem: Pessoa;
  equipe: readonly Pessoa[];
  aoFechar: () => void;
  aoMover: (recado: string) => void;
}): ReactNode {
  const api = useApi();
  const carteira = useCarteiraDaPessoa(origem.usuarioId, true);
  const [destino, definirDestino] = useState('');
  const [justificativa, definirJustificativa] = useState('');
  const [recusa, definirRecusa] = useState<string | null>(null);

  /*
   * O destino tem de estar ATIVO e ligado ao CRM.
   *
   * Mover para quem não pode atender só troca o nome do problema, e sem
   * vínculo no CRM não há para quem apontar — o servidor recusa nos dois
   * casos. Filtrar aqui evita oferecer uma escolha que vai ser negada.
   */
  const candidatos = equipe.filter(
    (pessoa) =>
      pessoa.usuarioId !== origem.usuarioId &&
      pessoa.status === 'ativo' &&
      pessoa.vinculoNoCrm === 'ligado',
  );

  const total = carteira.data?.negocios_em_aberto ?? 0;
  const impedimento = carteira.data?.impedimento ?? null;
  const podeEnviar =
    destino !== '' && justificativa.trim().length >= 10 && total > 0 && impedimento === null;

  const mover = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.redistribuirCarteira({
        corpo: {
          deUsuarioId: origem.usuarioId,
          paraUsuarioId: destino,
          // O número que a tela mostrou, e não um "tudo" — ver o cabeçalho.
          esperado: total,
          justificativa: justificativa.trim(),
        },
      });
    },
    onSuccess: (resultado) =>
      aoMover(
        resultado.avisos.length === 0
          ? `${resultado.transferidas} ${resultado.transferidas === 1 ? 'oportunidade movida' : 'oportunidades movidas'}.`
          : `${resultado.transferidas} movidos, com avisos: ${resultado.avisos.join(' · ')}`,
      ),
    onError: (erro: unknown) => {
      definirRecusa(recadoDeErro(erro, { generica: 'Não foi possível mover.' }));
      // A conta pode ter mudado: reler é o que faz a segunda tentativa valer.
      void carteira.refetch();
    },
  });

  return (
    <Dialogo
      titulo={`Carteira de ${origem.nome}`}
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            desabilitado={!podeEnviar || mover.isPending}
            motivo="Escolha para quem vai e escreva ao menos dez caracteres de justificativa."
            onClick={() => mover.mutate()}
          >
            {mover.isPending ? 'Movendo…' : `Mover ${total}`}
          </Botao>
        </>
      }
    >
      {carteira.isPending && <p>Contando as oportunidades em aberto…</p>}
      {carteira.isError && (
        <p className="dialogo__erro">Não foi possível contar a carteira. Tente de novo.</p>
      )}

      {carteira.data && (
        <>
          {impedimento ? (
            <p className="dialogo__erro" role="alert">
              {impedimento}
            </p>
          ) : (
            <p>
              <strong>
                {total} {total === 1 ? 'oportunidade em aberto' : 'oportunidades em aberto'}
              </strong>{' '}
              — só as etapas que esta organização declarou como abertas. As conversas{' '}
              <strong>não vão junto</strong>: elas ficam no nome de {origem.nome} e trocam de dono
              na próxima interação.
            </p>
          )}

          {!impedimento && total === 0 && <p className="admin__sem">Não há o que mover.</p>}
        </>
      )}

      {!impedimento && total > 0 && (
        <>
          <CampoSelecao
            rotulo="Passar para"
            value={destino}
            ajuda={
              candidatos.length === 0
                ? 'Ninguém na equipe está ativo e ligado ao CRM ao mesmo tempo.'
                : undefined
            }
            onChange={(e) => definirDestino(e.currentTarget.value)}
          >
            <option value="">Escolha…</option>
            {candidatos.map((pessoa) => (
              <option key={pessoa.usuarioId} value={pessoa.usuarioId}>
                {pessoa.nome} — {nomeDoPapel(pessoa.papel)}
              </option>
            ))}
          </CampoSelecao>

          <Campo
            rotulo="Por quê?"
            type="text"
            value={justificativa}
            maxLength={500}
            ajuda="Quem e quando a trilha registra. O porquê é o que ninguém reconstrói depois."
            onChange={(e) => definirJustificativa(e.currentTarget.value)}
          />
        </>
      )}

      {recusa && (
        <p className="dialogo__erro" role="alert">
          {recusa}
        </p>
      )}
    </Dialogo>
  );
}
